import { useCallback, useEffect, useState } from 'react';
import { Select } from 'antd';
import { makeStyles } from '@mui/styles';
import { Button, Dialog, DialogActions, DialogTitle } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { InitialPage } from '@components';
import { Credit, User } from '@types';
import { creditHttp, CreditsByUserLiteParams } from '@network';
import { FieldReport } from '@pages/cx/Components/FieldReport';

export const UserFarmsPage = ({ user }: {user: User}) => {
  const cls = useStyles();
  const form = useForm();

  const [credits, setCredits] = useState<Record<Credit['id'], Credit>>([]);
  const [selectedCreditId, setSelectedCreditId] = useState<Credit['id'] | null>(null);
  const [currentCreditId, setCurrentCreditId] = useState<Credit['id'] | null>(null);
  const [hasUpdated, setHasUpdated] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const onCreditChange = (value: Credit['id']) => {
    if (hasUpdated) {
      setIsDialogOpen(true);
      setSelectedCreditId(value);
    } else {
      setCurrentCreditId(value);
      setSelectedCreditId(value);
    }
  };

  const changeCredit = (creditId: Credit['id']) => {
    setCurrentCreditId(creditId);
    setIsDialogOpen(false);
  };

  const changeCreditConfirm = () => {
    if (!selectedCreditId) {
      return;
    }

    setHasUpdated(false);
    changeCredit(selectedCreditId);
  };

  const fetchCredits = useCallback(async () => {
    const params: CreditsByUserLiteParams = {
      userId: user.id,
      isAdminDraft: true,
      verqorCredit: 'mixed',
      getCx: true,
      getFarms: true,
      getFarmsCrops: true,
    };
    const nextCredits = await creditHttp.getCreditsLiteByUser(params);
    const nextCreditMap = {};
    const nextCreditsWithoutDraft = (nextCredits || []).filter(credit => credit.status !== 'draft');
    for (const credit of nextCreditsWithoutDraft) {
      nextCreditMap[credit.id] = credit;
    }

    setCredits(nextCreditMap);

    const [firstCredit] = nextCreditsWithoutDraft || [];
    if (firstCredit) {
      setCurrentCreditId(firstCredit.id);
    }
  }, []);

  useEffect(() => {
    void fetchCredits();
  }, [user.id, fetchCredits]);

  const backTo = `/user/${user.id}?initial`;

  const creditsOptions = Object.values(credits).map(credit => ({
    value: credit.id,
    label: `${credit.code}`,
  }));

  return (
    <InitialPage title="Farms" backTo={backTo} backText="Back to Individual User">
      <Select
        className={cls.select}
        placeholder="Credit"
        options={creditsOptions}
        onChange={onCreditChange}
        value={currentCreditId}
      />
      {currentCreditId && (
        <FormProvider {...form}>
          <FieldReport
            key={currentCreditId}
            credit={credits[currentCreditId]}
            hasUpdated={hasUpdated}
            setHasUpdated={setHasUpdated}
            fetchCredits={fetchCredits}
            showMap
          />
        </FormProvider>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Unsaved changes will be lost. Do you want to continue?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>
            No
          </Button>
          <Button onClick={changeCreditConfirm}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </InitialPage>
  );
};

const useStyles = makeStyles({
  select: {
    minWidth: 180,
    marginTop: 16,
    marginBottom: 16,

    '& [class*="ant-select-selector"]': {
      border: '1px solid #042E6B !important',
      borderRadius: '8px !important',
      fontSize: '15px !important',
      height: '37px !important',
    },

    '& [class*="ant-select-selection-placeholder"]': {
      height: '37px !important',
      lineHeight: '37px !important',
    },

    '& [class*="ant-select-selection-item"]': {
      height: '37px !important',
      lineHeight: '37px !important',
    },
  },
});
